/* eslint-disable no-param-reassign */
/* eslint-disable no-shadow */
import axios from 'axios'
import Cookies from 'js-cookie'
import { jwtDecode } from 'jwt-decode'
import * as Sentry from '@sentry/vue'
import helper from '~/global_helper/helpers.js'
import CONSTANTS from '~/global_helper/constants.js'

/* eslint-disable import/extensions */
import { configSettings } from '~/plugins/config-settings.js'

const LOGOUT = 'LOGOUT'
const SAVE_TOKEN = 'SAVE_TOKEN'
const FETCH_USER_SUCCESS = 'FETCH_USER_SUCCESS'
const FETCH_USER_FAILURE = 'FETCH_USER_FAILURE'
const FETCH_USER_PROMISE = 'FETCH_USER_PROMISE'
const FETCH_USER_PROMISE_CANCEL_TOKEN = 'FETCH_USER_PROMISE_CANCEL_TOKEN'
const SAVE_SIGN_UP_METADATA = 'SAVE_SIGN_UP_METADATA'
const SAVE_SESSION_METADATA = 'SAVE_SESSION_METADATA'
const AJAX_ERROR = 'AJAX_ERROR'
const SAVE_MFA_MODAL_DATA = 'SAVE_MFA_MODAL_DATA'
const FULFILL_MFA_PROMISE = 'FULFILL_MFA_PROMISE'
const FULFILL_MFA_PROMISE_CANCEL_TOKEN = 'FULFILL_MFA_PROMISE_CANCEL_TOKEN'

function getKeyFromToken(token) {
	if (!token) {
		return null
	}
	const userId = jwtDecode(token)?.sub
	return `su_${userId}`
}

// Helper function to build options for cookies
function buildOptions(base) {
	const opts = { path: '/', ...base }

	const host = window.location.hostname
	const isLocal = host === 'localhost' || host === '127.0.0.1'

	if (!isLocal) {
		opts.domain = `.${configSettings.appDomainNoProtocol}`
	}

	return opts
}
function setCookie(name, value, baseOpts = {}) {
	Cookies.set(name, value, buildOptions(baseOpts))
}

function removeCookie(name, baseOpts = {}) {
	Cookies.remove(name) // remove host-only if it exists
	Cookies.remove(name, buildOptions(baseOpts)) // remove domain cookie (prod/staging)
	// TODO: Remove after a while - probably after we move forward with v2 anyways lol (so that the stan.store/stanwith.me cookies are removed)
	// These were added by a previous commit, so we are just removing them here
	Cookies.remove(name, { domain: 'stan.store', path: '/' })
	Cookies.remove(name, { domain: 'stanwith.me', path: '/' })
}
// Each logged in account is given an account key in the following format `su_${userId}`
// When we go to login to a new account we track the last logged in key. So we can go back if we like.
// When we have a token being flagged as tmp, we add a flag so that when we change from that account we remove that account and data.
// To check if a user is logged in as an admin, we check if we are logged into a tmp account, and if one of our other accounts is an admin account.

// state
export const state = {
	user: null,
	token: Cookies.get('token'),
	current_account_key: Cookies.get('current_account_key') || getKeyFromToken(Cookies.get('token')),
	last_account_key: Cookies.get('last_account_key'),
	accounts: Object.keys(Cookies.get())
		.filter(key => /^su_.*/.test(key))
		.reduce((obj, key) => ({ ...obj, [key]: JSON.parse(Cookies.get(key) || '{}') }), {}),

	ajax_error: {
		message: '',
		errors: [],
	},
	signUpMetadata: {
		utm_source: Cookies.get('utm_source'),
		utm_medium: Cookies.get('utm_medium'),
		utm_campaign: Cookies.get('utm_campaign'),
		ref: Cookies.get('ref'),
	},
	subscribeFlags: {
		show_promo: Cookies.get('show_promo') === 'true',
		discount_code: Cookies.get('discount_code'),
		show_annual: Cookies.get('show_annual') === 'true',
		show_monthly: Cookies.get('show_monthly') === 'true',
	},
	hasActiveSubscription: false,
	sessionCountry: undefined,
	currentSubscriptionDetails: null,
	rate: null,
	currency: null,
	fetchUserPromise: null,
	fetchUserCancelToken: null,
	fetchUserPromiseResolve: null,
	weeklyInstagramPosts: 0,
	fulfillMFAPromise: null,
	fulfillMFAPromiseResolve: null,
	fulfillMFACancelToken: null,
	mfaLoginEndpoint: undefined,
	mfaToken: undefined,
	mfaConfigName: undefined,
}

export const getters = {
	user: state => state.user,
	token: state => state.token,
	addAccountFlag: state => state.current_account_key,
	current_account_key: state => state.current_account_key,
	last_account_key: state => state.last_account_key,
	// Will include all of the creators real accounts and tmp logged in accounts
	accounts: state => state.accounts,
	check: state => !!state.user,
	isSuperAdmin: state => state.user && state.user.list_of_roles.indexOf(1) > -1,
	isBillingAdmin: state => state.user && state.user.list_of_roles.indexOf(3) > -1,
	isLimitedAdmin: state => state.user && state.user.list_of_roles.indexOf(4) > -1,
	isAdminLoggedInAsUser: state => {
		// If currentely logged in account is a tmp account
		if (state.current_account_key && state.accounts[state.current_account_key]?.tmpLoginAsAdmin) {
			// If they have an admin account logged in
			return Object.keys(state.accounts).findIndex(key => key !== state.current_account_key && state.accounts[key]?.admin) > -1
		}
		return false
	},
	adminToken: state => {
		// Return token of any valid admin account
		const adminKey = Object.keys(state.accounts).find(key => state.accounts[key]?.admin)
		return adminKey ? state.accounts[adminKey].token : null
	},
	ajax_error: state => state.ajax_error,
	getSignUpMetadata: state => state.signUpMetadata,
	getSubscribeFlags: state => state.subscribeFlags,
	hasActiveSubscription: state => state.hasActiveSubscription,
	hasSubscriptionHistory: state => state.user?.has_subscription_history,
	niche: state => state.user?.tags['creator-niche']?.data['creator-niche'],
	hasSocialLinks: state => Object.keys(state.user?.data.socials).length > 0,
	sessionCountry: state => state.user?.data?.country_code || state.user?.tags?.location?.data?.country || state.sessionCountry,
	getSubscriptionDetails: state => state.currentSubscriptionDetails,
	getSubscriptionRate: state => state.rate,
	getSubscriptionCurrency: state => state.currency,
	userCountryCode: state => state?.user?.data?.country_code,
	weeklyInstagramPosts: state => state?.weeklyInstagramPosts,
	fulfillMFAPromise: state => state?.fulfillMFAPromise,
	fulfillMFAPromiseResolve: state => state?.fulfillMFAPromiseResolve,
	garyVeeChallengeRefferral: state =>
		state.user?.experiments?.gary_vee_challenge_experiment ||
		CONSTANTS.GARY_VEE_CHALLENGE.REFERRALS.includes(sessionStorage.getItem('ref')?.toLowerCase()),
}

// mutations
export const mutations = {
	// Add account flag is to track the account even after the user changes to another account
	[SAVE_TOKEN](state, { token, tmpLoginAsAdmin }) {
		const expires = new Date()
		expires.setDate(expires.getDate() + 30)
		state.token = token
		setCookie('token', token, { expires })

		// Clear accounts if we are not actively trying to login to a new account / Not an admin potentially logging into a new account
		const key = getKeyFromToken(token)

		if (key) {
			state.accounts[key] = {
				token,
				expires: expires.toISOString(),
				tmpLoginAsAdmin: tmpLoginAsAdmin || false,
			}

			// Update token
			state.last_account_key = state.current_account_key
			state.current_account_key = key

			// Update cookies
			setCookie(key, state.accounts[key], { expires })
			setCookie('last_account_key', state.last_account_key)
			setCookie('current_account_key', state.current_account_key)
		}
	},
	[SAVE_MFA_MODAL_DATA](state, { mfaLoginEndpoint, mfaToken, mfaConfigName }) {
		// update state
		state.mfaLoginEndpoint = mfaLoginEndpoint
		state.mfaToken = mfaToken
		state.mfaConfigName = mfaConfigName
	},
	[SAVE_SIGN_UP_METADATA](state, payload) {
		state.signUpMetadata = { ...state.signUpMetadata, ...payload }
		Object.keys(payload).forEach(key => {
			if (payload[key]) {
				setCookie(key, payload[key])
			}
		})
	},
	[SAVE_SESSION_METADATA](state, payload) {
		state.sessionMetadata = { ...state.sessionMetadata, ...payload }
		Object.keys(payload).forEach(key => {
			if (payload[key]) {
				sessionStorage.setItem(key, payload[key])
			}
		})
	},

	setSubscribeFlags(state, payload) {
		state.subscribeFlags = { ...state.subscribeFlags, ...payload }
		Object.keys(payload).forEach(key => {
			if (payload[key]) {
				setCookie(key, payload[key])
			}
		})
	},

	switchAccount(state, key) {
		if (state.accounts[key]) {
			const expires = state.accounts[key].expires
			const token = state.accounts[key].token

			state.token = token
			setCookie('token', token, { expires: new Date(expires) })

			// Check and clear last account if it was temporary
			if (state.accounts[state.current_account_key]?.tmpLoginAsAdmin) {
				delete state.accounts[state.current_account_key]
				removeCookie(state.current_account_key)
			} else {
				// Update old token
				state.last_account_key = state.current_account_key
			}
			state.current_account_key = key

			// Update cookies
			setCookie('last_account_key', state.last_account_key)
			setCookie('current_account_key', state.current_account_key)

			state.user = null
			return true
		}

		return false
	},

	prepareToAddAccount(state) {
		// All we need to add an account
		state.last_account_key = state.current_account_key
		state.current_account_key = null

		setCookie('last_account_key', state.last_account_key)
		removeCookie('current_account_key')
	},

	[FETCH_USER_SUCCESS](state, { user }) {
		state.user = user

		if (!state.user || !state.user?.subscriptions) {
			state.hasActiveSubscription = false
		} else {
			const platformSubscription = state.user?.subscriptions.platform
			const currentTimestamp = Math.floor(new Date().getTime() / 1000)
			const expiresSoon = platformSubscription ? platformSubscription.valid_until > currentTimestamp : false
			const hasActiveSubscription = platformSubscription && ([1, 2].includes(platformSubscription.status) || expiresSoon)
			state.hasActiveSubscription = !!hasActiveSubscription

			Sentry.setUser({ user_id: user.user_id, username: user.username })
		}

		// Check if current user is an admin
		const isAdmin = (state.user && state.user?.list_of_roles.indexOf(1) > -1) || (state.user && state.user?.list_of_roles.indexOf(4) > -1)
		const key = `su_${user.user_id}`
		if (state.accounts[key]) {
			state.accounts[key].email = user.email
			state.accounts[key].fullName = user.full_name
			state.accounts[key].user_id = user.user_id
			state.accounts[key].username = user.username
			state.accounts[key].image = user?.data?.profile_image
			state.accounts[key].admin = isAdmin

			setCookie(key, state.accounts[key], { expires: new Date(state.accounts[key].expires) })
		}

		// Enrich user object with experiment-based flags
		if (state.user && state.user.experiments) {
			// has_dynamic_dashboard: Enable if user has dynamic_dashboard_v4 or higher
			const dynamicDashboardVersions = [
				'dynamic_dashboard_v4',
				'dynamic_dashboard_v5',
				'dynamic_dashboard_v6',
				'dynamic_dashboard_v7',
				'dynamic_dashboard_v8',
			]
			state.user.has_dynamic_dashboard = dynamicDashboardVersions.some(version => state.user.experiments[version] === 1)
		}
	},

	[FETCH_USER_FAILURE](state) {
		state.token = null
		state.hasActiveSubscription = false
		removeCookie('token')

		// Delete current users data
		delete state.accounts[state.current_account_key]
		removeCookie(state.current_account_key)
		removeCookie('current_account_key')

		state.last_account_key = null
		state.current_account_key = null
	},
	[FETCH_USER_PROMISE](state, { promise, resolve }) {
		state.fetchUserPromise = promise
		state.fetchUserPromiseResolve = resolve
	},
	[FETCH_USER_PROMISE_CANCEL_TOKEN](state, { source }) {
		state.fetchUserCancelToken = source
	},

	[LOGOUT](state) {
		state.user = null
		state.hasActiveSubscription = false
		state.token = null
		removeCookie('token')

		// Clear all signed in accounts
		Object.keys(state.accounts).forEach(key => removeCookie(key))
		removeCookie('last_account_key')
		removeCookie('current_account_key')

		state.accounts = {}
		state.last_account_key = null
		state.current_account_key = null
	},

	[FULFILL_MFA_PROMISE](state, { promise, resolve }) {
		state.fulfillMFAPromise = promise
		state.fulfillMFAPromiseResolve = resolve
	},

	[FULFILL_MFA_PROMISE_CANCEL_TOKEN](state, { source }) {
		state.fulfillMFACancelToken = source
	},

	[AJAX_ERROR](state, payload) {
		const message = payload.message || payload.response?.data?.message
		const errors = payload.errors || payload.response?.data?.errors
		state.ajax_error = {
			message,
			errors,
		}
	},

	resetState(state) {
		state.ajax_error = { message: '', errors: [] }
	},

	setSessionCountry(state, country) {
		state.sessionCountry = country
	},
	SET_SUBSCRIPTION_DETAILS(state, subscriptionDetails) {
		state.currentSubscriptionDetails = subscriptionDetails
	},
	SET_SUBSCRIPTION_RATE(state, rate) {
		state.rate = rate
	},
	SET_SUBSCRIPTION_CURRENCY(state, currency) {
		state.currency = currency
	},
	setWeeklyInstagramPosts(state, postCount) {
		state.weeklyInstagramPosts = postCount
	},
}

// Action helper
const fetchUserInternal = (state, commit, fetchUserResolve) => {
	// Cancel previous request if active
	if (state.fetchUserCancelToken) {
		state.fetchUserCancelToken.cancel('New Request')
	}

	// Store cancel token, so we can cancel this request if a new one comes in
	const source = axios.CancelToken.source()
	commit(FETCH_USER_PROMISE_CANCEL_TOKEN, { source })

	axios
		.get('v1/users/get_user', {
			cancelToken: source.token,
		})
		.then(response => {
			const { data: user } = response
			if (user.data.color_code) document.documentElement.style.setProperty('--stan-user-primary-color', user.data.color_code)
			if (user.profile_image) document.documentElement.style.setProperty('--stan-store-background-url', `url(${user.profile_image})`)
			commit(FETCH_USER_SUCCESS, { user })
			window.user = user
			// Finalize and resolve outer promise
			fetchUserResolve(user)
			commit(FETCH_USER_PROMISE, { promise: null, resolve: null })
			commit(FETCH_USER_PROMISE_CANCEL_TOKEN, { source: null })
		})
		.catch(error => {
			if (axios.isCancel(error)) {
				// We are retrying
			} else {
				console.error('fetchUserError', error)
				commit(FETCH_USER_FAILURE)
				commit(FETCH_USER_PROMISE, { promise: null, resolve: null })
				commit(FETCH_USER_PROMISE_CANCEL_TOKEN, { source: null })
				fetchUserResolve() // You might resolve or reject here depending on desired behavior
			}
		})
}

// actions
export const actions = {
	async fetchSessionCountry({ commit }) {
		let ipInfo = JSON.parse(Cookies.get('ip_info') || '{}')
		if (!ipInfo?.country) {
			try {
				const response = await axios.get('v1/services/get-ip-info')
				ipInfo = response.data
				setCookie('ip_info', ipInfo, { expires: 30 })
			} catch (error) {
				// eslint-disable-next-line no-console
				console.log(`Could not automatically determine the country based on IP address. Error: ${error}`)
			}
		}
		commit('setSessionCountry', ipInfo.country)
		return ipInfo
	},
	login({ commit }, payload) {
		const customError = {}

		const payloadUse = {
			email_or_username: payload?.email_or_username.trim(),
			password: payload?.password,
			version: 2,
		}

		return new Promise((resolve, reject) => {
			axios
				.post('v1/login', payloadUse, { baseURL: configSettings.authApiUrl })
				.then(response => {
					// check if MFA is required.
					if (response.data?.mfa_token) {
						// So that when the Modal is shown we can grab it's params from the state
						commit(SAVE_MFA_MODAL_DATA, {
							mfaLoginEndpoint: helper.removePrefix(response.data?.route, '/api/'),
							mfaToken: response.data?.mfa_token,
							mfaConfigName: response.data?.config_name,
						})

						// use the temporary token to make the next MFA request
						commit(SAVE_TOKEN, { token: response.data.mfa_token })

						// mechanisms to inform dependent flows about the state of mfa
						let fulfillMFAPromise = state.fulfillMFAPromise
						let fulfillMFAResolve = state.fulfillMFAResolve
						if (!fulfillMFAPromise) {
							// Outer promise that all callers await
							fulfillMFAPromise = new Promise(resolve => {
								// expose an external way to resolve the promise
								fulfillMFAResolve = resolve
							})

							commit(FULFILL_MFA_PROMISE, { promise: fulfillMFAPromise, resolve: fulfillMFAResolve })
						}

						// Cancel previous request if active
						if (state.fulfillMFACancelToken) {
							state.fulfillMFACancelToken.cancel('New Request')
						}

						// Store cancel token, so we can cancel this request if a new one comes in
						const source = axios.CancelToken.source()
						commit(FULFILL_MFA_PROMISE_CANCEL_TOKEN, { source })
					} else {
						commit(SAVE_TOKEN, { token: response.data.token })
					}
					resolve(response)
				})
				.catch(error => {
					if (error.response === undefined) {
						customError.message = 'Something went wrong'
						customError.errors = []
						customError.status = 'error'
						commit(AJAX_ERROR, customError)
					} else {
						commit(AJAX_ERROR, error)
					}
					reject(error)
				})
				.finally(() => {
					// commit('setLoading', false)
				})
		})
	},
	cancelFetchUser({ state }) {
		/** Cancel active fetch user request, use this if you need to enforce a dirty state temporarily
		 * NOTE: Anywhere that is waiting for `fetchUser` to resolve will be blocked until you call `fetchUser` again and allow it to complete
		 */
		if (state.fetchUserCancelToken) {
			state.fetchUserCancelToken.cancel('New Request')
		}
	},
	fetchUser({ state, commit }) {
		/**
		 * This function fetches user data async, and enforces 1 request at a time. This is to avoid race conditions for our dirty user object and requests expecting new data.
		 * This works by returning a generic promise, while internally handling request cancellation as new fetch user requests come in
		 */

		let fetchUserPromise = state.fetchUserPromise
		let fetchUserResolve = state.fetchUserPromiseResolve
		if (!fetchUserPromise) {
			// Outer promise that all callers await
			fetchUserPromise = new Promise(resolve => {
				fetchUserResolve = resolve
			})

			commit(FETCH_USER_PROMISE, { promise: fetchUserPromise, resolve: fetchUserResolve })
		}

		fetchUserInternal(state, commit, fetchUserResolve)

		return fetchUserPromise
	},
	updateProfile(_, payload) {
		// Prifile data updates as a payload dump and backend will take care of the rest
		const data = payload

		if (data.instagram) {
			data.instagram = helper.getSocialHandle(data.instagram, 'instagram.com')
			data.instagram = helper.getSocialHandle(data.instagram, '@')
		}

		if (data.tiktok) {
			data.tiktok = helper.getSocialHandle(data.tiktok, 'tiktok.com/@')
			data.tiktok = helper.getSocialHandle(data.tiktok, 'tiktok.com')
			data.tiktok = helper.getSocialHandle(data.tiktok, '@')
		}
		return axios.post('v1/users/profile-page-setup', data)
	},

	async logout({ commit, state }) {
		const token = state.token
		commit(LOGOUT)

		// call the backend to invalidate the token
		// will throw 401 for V1
		axios.post(
			'v1/logout',
			{},
			{
				baseURL: configSettings.authApiUrl,
				headers: {
					Authorization: `Bearer ${token}`,
				},
			}
		)
	},

	raiseAjaxError({ commit }, payload) {
		commit(AJAX_ERROR, payload)
	},

	saveToken({ commit }, payload) {
		commit(SAVE_TOKEN, payload)
	},

	saveSignUpMetadata({ commit }, payload) {
		commit(SAVE_SIGN_UP_METADATA, payload)
	},

	saveSessionMetadata({ commit }, payload) {
		commit(SAVE_SESSION_METADATA, payload)
	},

	saveSubscribeFlags({ commit }, payload) {
		commit('setSubscribeFlags', payload)
	},

	resetState: ({ commit }) => {
		commit('resetState')
	},
	async getPlatformSubscription({ commit, state }) {
		// Check if subscription data already exists in the state
		if (!state.currentSubscriptionDetails) {
			try {
				const response = await axios.get('v1/users/subscriptions-platform')
				const data = response.data

				// Commit the response data to the Vuex state
				commit('SET_SUBSCRIPTION_DETAILS', data)
				commit('SET_SUBSCRIPTION_RATE', data.price.amount)
				commit('SET_SUBSCRIPTION_CURRENCY', data.price.currency)
			} catch (error) {
				console.error('Error fetching subscription:', error)

				// Fallback to fetching user info if the subscription fails
				await this.dispatch('Auth/fetchUser')
			}
		}
	},
	fetchWeeklyInstagramPosts({ commit }) {
		axios
			.get('v1/automations/weekly-instagram-posts')
			.then(response => {
				commit('setWeeklyInstagramPosts', response.data.post_count)
			})
			.catch(error => {
				// eslint-disable-next-line no-console
				console.log(`Could not get Instagram posts. Error: ${error}`)
			})
	},
	upgradeURLAuthToken({ commit }, urlToken) {
		// Upgrade the auth token from V1 to V2
		return axios
			.get('v1/authentication/upgrade-auth-token', {
				baseURL: configSettings.authApiUrl,
				params: {
					token: urlToken,
				},
			})
			.then(response => {
				localStorage.clear()
				commit(SAVE_TOKEN, { token: response.data.token })
				return response.data.token
			})
			.catch(error => {
				if (error.response?.status === 401) {
					throw new Error('Invalid or expired token')
				}
				throw error
			})
	},
}
